import { X, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

export default function CatalogFilters({
  filters,
  region,
  grape,
  sortBy,
  sortOrder,
  yearRange,
  priceRange,
  yearBounds,
  priceBounds,
  hasFilters,
  onRegionChange,
  onGrapeChange,
  onSortChange,
  onYearRangeChange,
  onPriceRangeChange,
  onClear,
}) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const canSortByScore = !!user;
  const canSortByPrice = !!user;

  return (
    <div className="glass-card rounded-sm p-5 space-y-5" data-testid="catalog-filters">
      <div className="flex flex-wrap items-center gap-4">
        <Select value={region || "all"} onValueChange={onRegionChange}>
          <SelectTrigger data-testid="filter-region" className="w-[200px] bg-transparent border-white/10 text-white/70 font-body text-sm h-9 rounded-sm">
            <SelectValue placeholder={t.allRegions} />
          </SelectTrigger>
          <SelectContent className="bg-wine-paper border-white/10 max-h-[320px]">
            <SelectItem value="all" className="text-white/70 font-body text-sm">{t.allRegions}</SelectItem>
            {filters.regions.map((r) => (
              <SelectItem key={r} value={r} className="text-white/70 font-body text-sm">{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={grape || "all"} onValueChange={onGrapeChange}>
          <SelectTrigger data-testid="filter-grape" className="w-[200px] bg-transparent border-white/10 text-white/70 font-body text-sm h-9 rounded-sm">
            <SelectValue placeholder={t.allGrapes} />
          </SelectTrigger>
          <SelectContent className="bg-wine-paper border-white/10 max-h-[320px]">
            <SelectItem value="all" className="text-white/70 font-body text-sm">{t.allGrapes}</SelectItem>
            {filters.grapes.map((g) => (
              <SelectItem key={g} value={g} className="text-white/70 font-body text-sm">{g}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={`${sortBy}-${sortOrder}`} onValueChange={onSortChange}>
          <SelectTrigger data-testid="filter-sort" className="w-[180px] bg-transparent border-white/10 text-white/70 font-body text-sm h-9 rounded-sm">
            <SelectValue placeholder="Sort By" />
          </SelectTrigger>
          <SelectContent className="bg-wine-paper border-white/10">
            <SelectItem value="name-asc" className="text-white/70 font-body text-sm">{t.nameAZ}</SelectItem>
            <SelectItem value="name-desc" className="text-white/70 font-body text-sm">{t.nameZA}</SelectItem>
            {canSortByPrice && (
              <>
                <SelectItem value="price-asc" className="text-white/70 font-body text-sm">{t.priceLowHigh}</SelectItem>
                <SelectItem value="price-desc" className="text-white/70 font-body text-sm">{t.priceHighLow}</SelectItem>
              </>
            )}
            <SelectItem value="year-desc" className="text-white/70 font-body text-sm">{t.newestFirst}</SelectItem>
            <SelectItem value="year-asc" className="text-white/70 font-body text-sm">{t.oldestFirst}</SelectItem>
            {canSortByScore ? (
              <SelectItem value="rating-desc" className="text-white/70 font-body text-sm">{t.topRated}</SelectItem>
            ) : (
              <SelectItem value="rating-desc" disabled className="text-white/40 font-body text-sm">
                <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> {t.topRated} · Sign in</span>
              </SelectItem>
            )}
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button variant="ghost" onClick={onClear} data-testid="clear-filters" className="text-merlot-light hover:text-white text-sm font-body gap-1">
            <X className="w-3 h-3" /> {t.clear}
          </Button>
        )}
      </div>

      {yearBounds && priceBounds && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-white/5">
          <div data-testid="filter-year-range">
            <div className="flex items-center justify-between mb-3">
              <label className="font-body text-[11px] text-white/40 tracking-wider uppercase">Year</label>
              <span className="font-accent text-xs text-gold/70">{yearRange[0]} – {yearRange[1]}</span>
            </div>
            <Slider
              min={yearBounds.min}
              max={yearBounds.max}
              step={1}
              value={yearRange}
              onValueChange={onYearRangeChange}
              className="[&_[role=slider]]:border-merlot-light [&_.bg-primary]:bg-merlot [&_.bg-primary\\/20]:bg-white/10"
            />
          </div>
          {user ? (
            <div data-testid="filter-price-range">
              <div className="flex items-center justify-between mb-3">
                <label className="font-body text-[11px] text-white/40 tracking-wider uppercase">Price (USD)</label>
                <span className="font-accent text-xs text-gold/70">${priceRange[0]} – ${priceRange[1]}</span>
              </div>
              <Slider
                min={priceBounds.min}
                max={priceBounds.max}
                step={5}
                value={priceRange}
                onValueChange={onPriceRangeChange}
                className="[&_[role=slider]]:border-merlot-light [&_.bg-primary]:bg-merlot [&_.bg-primary\\/20]:bg-white/10"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center bg-black/20 rounded-sm py-4 text-white/40">
              <Lock className="w-3 h-3 mr-2" />
              <span className="font-body text-xs">Sign in to filter by price</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
